namespace $ {

	type $shm_hitalama_map_history_runtime_context = {
		map: $shm_hitalama_map
		map_id: string
		id: string
		title: string
		source: $shm_hitalama_map_history_source
		changes: $shm_hitalama_map_history_change[]
		data?: $shm_hitalama_map_history_data
		slots?: Map< object | string, Map< string, unknown > >
	}

	type $shm_hitalama_map_history_runtime_pending = {
		context: $shm_hitalama_map_history_runtime_context
		owner: object | null
		result?: any
		done: boolean
		task?: ( context: $shm_hitalama_map_history_runtime_context )=> any
	}

	export class $shm_hitalama_map_history_runtime {

		private static context: $shm_hitalama_map_history_runtime_context | null = null
		private static muted = 0
		private static sequence = 0
		private static writer_session = $mol_guid( 16 )
		private static pending_transactions = new Map< string, $shm_hitalama_map_history_runtime_pending >
		private static edit_sessions = new Map< string, $shm_hitalama_map_history_runtime_context >
		private static geometry_sessions = new Map< string, {
			id: string
			map: $shm_hitalama_map
			context: $shm_hitalama_map_history_runtime_context
			widgets: Map< string, {
				target: $shm_hitalama_map_widget
				before: Record< string, number >
			} >
		} >()

		static value( value: any ): any {
			if( value === undefined ) return null
			if( value === null || typeof value !== 'object' ) return value
			if( value instanceof $giper_baza_link ) return value.str
			if( Array.isArray( value ) ) return value.map( item => this.value( item ) )
			if( value.constructor === Object ) return Object.fromEntries(
				Object.entries( value ).map( ([ key, item ])=> [ key, this.value( item ) ] ),
			)
			return String( value )
		}

		static equal( left: any, right: any ) {
			return JSON.stringify( this.value( left ) ) === JSON.stringify( this.value( right ) )
		}

		/** Physical writer, independent of the authorization/undo owner. */
		static writer_id( peer: string ) {
			return `session:${ peer }:${ this.writer_session }`
		}

		static slot< Value >( owner: object | string | null, key: string, make: ()=> Value ): Value {
			if( !owner || !this.context ) return make()
			const slots = this.context.slots ??= new Map
			let owner_slots = slots.get( owner )
			if( !owner_slots ) slots.set( owner, owner_slots = new Map )
			if( owner_slots.has( key ) ) return owner_slots.get( key ) as Value
			const value = make()
			owner_slots.set( key, value )
			return value
		}

		@ $mol_action
		static next_id( map: $shm_hitalama_map ) {
			const peer = map.$.$giper_baza_auth.current().pass().peer().str
			// Two tabs of one auth have independent counters and may share the
			// same millisecond. Their immutable command addresses must not collide.
			return `${ peer }:${ Date.now() }:${ ++ this.sequence }:${ $mol_guid( 16 ) }`
		}

		static transaction_context() {
			return this.context
		}

		static transaction_prepare(
			map: $shm_hitalama_map,
			title: string,
			source: $shm_hitalama_map_history_source,
			id: string,
			owner: object | null,
		): $shm_hitalama_map_history_runtime_pending {
			// Geometry/edit sessions hand their already captured changes to the
			// closing command. Adopt that explicit hand-off, not another writer.
			const session = this.pending_transactions.get( map.link().str )
			if( session?.context.id === id ) return session
			if( session?.done && !session.task && session.owner === owner ) return session
			return { context: { map, map_id: map.link().str, id, title, source, changes: [] }, owner, done: false }
		}

		static transaction< Result >(
			map: $shm_hitalama_map | null | undefined,
			title: string,
			task: ( context: $shm_hitalama_map_history_runtime_context | null )=> Result,
			source: $shm_hitalama_map_history_source = 'user',
			id?: string,
		) {
			if( !map ) return task( null )
			// A nested writer may resume independently after its parent's silence()
			// stack has unwound. Capture the mode once in that writer's wire task,
			// then re-enter silence on every replay, not just the initial call.
			if( $mol_wire_sync( this ).is_muted() ) return this.silence( ()=> task( null ) )
			const owner = $mol_wire_auto()
			// A child action can resume before its parent re-enters this stack.
			// Retain its original enclosing transaction, just as we retain silence.
			const parent = $mol_wire_sync( this ).transaction_context()
			const current = this.context
			const map_id = map.link().str
			if( parent?.map_id === map_id ) {
				this.context = parent
				try { return task( parent ) }
				finally { this.context = current }
			}
			if( this.geometry_sessions.has( map_id ) ) return task( current )
			const edit = this.edit_sessions.get( map_id )
			if( edit ) {
				if( edit.changes.length === 0 ) {
					edit.title = title
					edit.source = source
				}
				this.context = edit
				try {
					return task( edit )
				} finally {
					this.context = current
				}
			}
			const command_id = id ?? $mol_wire_sync( this ).next_id( map )
			// The command's wire slot owns its pending mutation and result. A single
			// map slot cannot own concurrent commands: another writer may suspend
			// while committing and displace it, causing the first mutation to replay.
			const pending = $mol_wire_sync( this ).transaction_prepare( map, title, source, command_id, owner )
			const context = pending.context
			pending.task ??= task
			this.pending_transactions.set( map_id, pending )
			this.context = context
			try {
				// Keep the command slot even while a completed mutation awaits commit.
				// Skipping it on replay shifts every subsequent wire call.
				pending.result = $mol_wire_sync( this ).transaction_run( pending )
				pending.done = true
				if( context.changes.length ) $mol_wire_sync( this ).commit( context )
				if( this.pending_transactions.get( map_id ) === pending ) this.pending_transactions.delete( map_id )
				return pending.result as Result
			} catch( error ) {
				if( !$mol_promise_like( error ) && this.pending_transactions.get( map_id ) === pending ) this.pending_transactions.delete( map_id )
				throw error
			} finally {
				this.context = current
			}
		}

		static transaction_run( pending: $shm_hitalama_map_history_runtime_pending ) {
			const current = this.context
			this.context = pending.context
			try { return pending.task!( pending.context ) }
			finally { this.context = current }
		}

		static commit( context: $shm_hitalama_map_history_runtime_context ) {
			context.data ??= {
				id: context.id,
				title: context.title,
				source: context.source,
				changes: context.changes,
			}
			return context.map.history().event_add( context.data )
		}

		static capture(
			map: $shm_hitalama_map | null | undefined,
			change: $shm_hitalama_map_history_change,
			title = change.type,
			source: $shm_hitalama_map_history_source = 'user',
		) {
			if( !map ) return
			if( this.muted || this.equal( change.before, change.after ) ) return
			if(
				change.type === 'widget_set'
					&& change.before?.constructor === Object
					&& change.after?.constructor === Object
					&& Object.keys( change.after ).length > 1
			) {
				for( const property of Object.keys( change.after ) ) this.capture( map, {
					... change,
					before: { [ property ]: change.before[ property ] },
					after: { [ property ]: change.after[ property ] },
					meta: { ... change.meta, property },
				}, title, source )
				return
			}
			const normalized = this.value( change ) as $shm_hitalama_map_history_change
			const map_id = map.link().str
			const pending = this.pending_transactions.get( map_id )
			const context = this.context?.map_id === map_id
				? this.context
				: this.geometry_sessions.get( map_id )?.context
					?? this.edit_sessions.get( map_id )
						?? ( pending?.done ? null : pending?.context )
			if( context ) {
				const slot = this.change_slot( normalized )
				const previous = slot && [ ... context.changes ].reverse().find( item => this.change_slot( item ) === slot )
				if( previous ) {
					previous.after = normalized.after
					previous.targets = [ ... new Set([ ... previous.targets ?? [], ... normalized.targets ?? [] ]) ]
					previous.automatic &&= normalized.automatic
					if( this.equal( previous.before, previous.after ) ) {
						context.changes.splice( context.changes.indexOf( previous ), 1 )
					}
				} else {
					context.changes.push( normalized )
				}
				return
			}
			this.transaction( map, title, context => context!.changes.push( normalized ), source )
		}

		private static change_slot( change: $shm_hitalama_map_history_change ) {
			switch( change.type ) {
				case 'widget_set': return `${ change.type }:${ change.target }:${ change.meta?.property ?? Object.keys( change.after ?? {})[0] ?? '' }`
				case 'cell_set': return `${ change.type }:${ change.target }:${ change.meta?.field ?? '' }`
				case 'field_set': return `${ change.type }:${ change.target }:${ change.meta?.property ?? '' }`
				case 'rows_add':
				case 'rows_remove': return Array.isArray( change.meta?.rows )
					? `${ change.type }:${ change.target }:${ JSON.stringify( change.meta.rows.map( ( row: { record: string } )=> row.record ) ) }`
					: ''
				case 'rows_update': return Array.isArray( change.meta?.cells )
					? `${ change.type }:${ change.target }:${ JSON.stringify( change.meta.cells.map( ( cell: { record: string, field: string } )=> [ cell.record, cell.field ] ) ) }`
					: ''
				default: return ''
			}
		}

		static silence< Result >( task: ()=> Result ) {
			++ this.muted
			try {
				return task()
			} finally {
				-- this.muted
			}
		}

		static is_muted() {
			return Boolean( this.muted )
		}

		static edit_begin( map: $shm_hitalama_map ) {
			const map_id = map.link().str
			if( this.edit_sessions.has( map_id ) ) return
			this.edit_sessions.set( map_id, {
				map,
				map_id,
				id: `edit:${ $mol_guid( 16 ) }`,
				title: 'Редактирование текста',
				source: 'user',
				changes: [],
			} )
		}

		static edit_context( map: $shm_hitalama_map ) {
			return this.edit_sessions.get( map.link().str )
		}

		@ $mol_action
		static edit_end( map: $shm_hitalama_map ) {
			const map_id = map.link().str
			// A focusout action can suspend while the next input starts a new edit
			// session. Its retry must close only the session captured by this action.
			const context = $mol_wire_sync( this ).edit_context( map )
			if( !context ) return
			if( this.edit_sessions.get( map_id ) === context ) this.edit_sessions.delete( map_id )
			if( !context.changes.length ) return
			return this.commit( context )
		}

		private static geometry_values( widget: $shm_hitalama_map_widget ) {
			const data = widget.render_data()
			// Upper/left handles store offsets separately from the lower/right
			// edges. A complete gesture needs all six values, not just Render_data.
			return {
				body_x: data.body_x,
				body_y: data.body_y,
				right_edge_x: data.right_edge_x,
				bottom_edge_y: data.bottom_edge_y,
				top_edge_y: widget.top_edge_y(),
				left_edge_x: widget.left_edge_x(),
			}
		}

		static geometry_begin( widget: $shm_hitalama_map_widget ) {
			const map = widget.map()
			if( !map ) return
			const map_id = map.link().str
			let session = this.geometry_sessions.get( map_id )
			if( !session ) {
				const id = `geometry:${ $mol_guid( 16 ) }`
				this.geometry_sessions.set( map_id, session = {
				id,
				map,
				context: {
					map,
					map_id,
					id,
					title: 'Перемещение или размер виджета',
					source: 'user',
					changes: [],
				},
				widgets: new Map,
				} )
			}
			const widget_id = widget.link().str
			if( !session.widgets.has( widget_id ) ) session.widgets.set( widget_id, {
				target: widget,
				before: this.geometry_values( widget ),
			} )
		}

		static geometry_begin_many( widgets: readonly $shm_hitalama_map_widget[] ) {
			const visited = new Set< string >
			const visit = ( widget: $shm_hitalama_map_widget )=> {
				const id = widget.link().str
				if( visited.has( id ) ) return
				visited.add( id )
				this.geometry_begin( widget )
				for( const child of widget.widgets() ) visit( child )
			}
			for( const widget of widgets ) visit( widget )
		}

		static geometry_active( widget: $shm_hitalama_map_widget ) {
			const map = widget.map()
			return Boolean( map && this.geometry_sessions.get( map.link().str )?.widgets.has( widget.link().str ) )
		}

		static geometry_end( widget: $shm_hitalama_map_widget ) {
			const map = widget.map()
			if( !map ) return
			const session = this.geometry_sessions.get( map.link().str )
			if( session && !session.widgets.has( widget.link().str ) ) return
			this.geometry_end_map( map )
		}

		static geometry_end_map( map: $shm_hitalama_map ) {
			const map_id = map.link().str
			const session = this.geometry_sessions.get( map_id )
			if( !session ) {
				const pending = this.pending_transactions.get( map_id )
				if( pending?.done && pending.context.title === 'Перемещение или размер виджета' ) {
					return this.transaction( map, pending.context.title, ()=> undefined )
				}
				return
			}

			const current = this.context
			this.context = session.context
			try {
				for( const { target, before } of session.widgets.values() ) this.capture( session.map, {
					type: 'widget_set',
					target: target.link().str,
					before,
					after: this.geometry_values( target ),
				}, 'Перемещение или размер виджета' )
			} finally {
				this.context = current
			}
			this.geometry_sessions.delete( map_id )
			if( !session.context.changes.length ) return
			this.pending_transactions.set( map_id, {
				context: session.context,
				owner: $mol_wire_auto(),
				done: true,
			} )
			return this.transaction( map, session.context.title, ()=> undefined )
		}

	}

}
